/* ==========================================================================
   Horizon-style quick add for NoCosign

   Additive by design. The theme's own add-to-cart and quick-cart-drawer are
   left intact - this only:
     1. swaps the black + for Horizon's cart icon and applies the pill styling
     2. measures the pill's expanded width so the growth can animate
     3. shows Horizon's confirmation tick after an add
     4. plays Horizon's fly-to-cart animation, from three places:
          product cards, the quick-cart modal, and the main product page

   The bag and the tick live in the same CSS grid cell inside .hqa-icon. That is
   deliberate: stacking them with absolute positioning would need the button to
   be position: relative, and the theme already declares top:-10px; left:-10px
   on it. Those offsets are inert while it is static, so making it relative
   shifts the button 10px up and left.

   Deleting this file and its three lines in theme.liquid restores the original
   cards exactly.
   ========================================================================== */

(function () {
  'use strict';

  var BTN = '.product-card__add-to-cart--button';
  var CARD = '.product-card';
  var MEDIA = '.product-card__media';
  var CART_TARGET = '.header__utils-link--cart';
  var LABEL = '.product-card__add-to-cart--text';

  var PDP_BTN = 'product-form button[name="add"], .product-form__submit';
  var PDP_MEDIA = '.product-grid__item--media, .product__gallery-toggle, .product__media, .product__media-wrapper';

  var ADD_TEXT = 'Add to cart';
  var CHOOSE_TEXT = 'Choose';

  var CHECK_DELAY = 160;
  var CHECK_HOLD = 1100;

  /* Horizon's cart glyph (assets/icon-cart.svg), symmetrical about the vertical
     axis. The viewBox is tightened from "0 0 20 20" to the glyph's ink box
     (getBBox: x=3.392 w=13.216, plus 1.4 stroke -> x=2.692 w=14.616 h=15.462;
     squared and centred on 10,10). Without this the glyph carried ~3px of dead
     space each side and the pill looked left-heavy despite equal padding. */
  var ICON =
    '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="2.269 2.269 15.462 15.462" fill="none">' +
    '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4" ' +
    'd="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/>' +
    '</svg>';

  /* Confirmation tick. Path length is about 16 units, so the stroke-dasharray
     of 22 in the stylesheet fully hides it before the draw animation runs. */
  var CHECK =
    '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">' +
    '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" ' +
    'd="M4.5 10.4 8.3 14.2 15.5 6.4"/>' +
    '</svg>';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------- measurement */

  function readVar(el, name, fallback) {
    var v = parseFloat(getComputedStyle(el).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  /**
   * CSS cannot animate width towards `auto`, so the expanded size must be a
   * real number. This mirrors the CSS box exactly:
   *   padding-left + icon + gap + label + padding-right + borders
   */
  function measure(button) {
    var label = button.querySelector(LABEL);
    if (!label) return;

    var labelWidth = label.scrollWidth;
    if (!labelWidth) return;

    var icon = readVar(button, '--hqa-icon', 19);
    var padL = readVar(button, '--hqa-pad-l', 15);
    var padR = readVar(button, '--hqa-pad-r', 16);
    var gap = readVar(button, '--hqa-gap', 9);
    var border = 4;

    button.style.setProperty('--hqa-label-w', labelWidth + 'px');
    button.style.setProperty(
      '--hqa-expanded',
      Math.ceil(padL + icon + gap + labelWidth + padR + border) + 'px'
    );
  }

  function measureAll() {
    document.querySelectorAll('.hqa-button').forEach(measure);
  }

  /* ---------------------------------------------------------------- enhance */

  function enhance(button) {
    if (!button || button.dataset.hqa === '1') return;

    // Grid cards only. The quick-cart modal reuses these class names for its
    // own full-size buttons, which must keep the theme's styling.
    if (button.closest('.product-card__quick-cart') || button.closest('quick-cart-drawer')) return;

    button.dataset.hqa = '1';
    button.classList.add('hqa-button');

    var oldSvg = button.querySelector('svg');
    if (oldSvg && !button.querySelector('.hqa-icon')) {
      var wrap = document.createElement('span');
      wrap.className = 'hqa-icon';
      wrap.setAttribute('aria-hidden', 'true');
      // Bag and tick share one grid cell - see the note at the top of the file.
      wrap.innerHTML =
        '<span class="hqa-glyph">' + ICON + '</span>' +
        '<span class="hqa-check">' + CHECK + '</span>';
      oldSvg.parentNode.replaceChild(wrap, oldSvg);
    }

    var label = button.querySelector(LABEL);
    if (label && !label.dataset.hqaLabel) {
      label.dataset.hqaLabel = '1';
      label.textContent = button.classList.contains('quick-cart-drawer__trigger')
        ? CHOOSE_TEXT
        : ADD_TEXT;
    }

    measure(button);
  }

  function enhanceAll(root) {
    (root || document).querySelectorAll(BTN).forEach(enhance);
  }

  /** Collapse to a circle, draw the tick, hold, then return to normal. */
  function confirm(button) {
    if (!button) return;
    if (button.dataset.hqaConfirming === '1') return;
    button.dataset.hqaConfirming = '1';

    var show = setTimeout(function () {
      button.classList.add('hqa-done');
    }, CHECK_DELAY);

    setTimeout(function () {
      clearTimeout(show);
      button.classList.remove('hqa-done');
      // Reset the tick so the draw replays on the next add.
      var path = button.querySelector('.hqa-check svg path');
      if (path) {
        path.style.animation = 'none';
        void path.offsetWidth;
        path.style.animation = '';
      }
      button.dataset.hqaConfirming = '';
    }, CHECK_DELAY + CHECK_HOLD);
  }

  /* ------------------------------------------------------------- fly to cart */

  /** Largest visible image inside a scope - the hero shot, not a thumbnail. */
  function biggestImage(scope) {
    if (!scope) return null;
    var imgs = scope.querySelectorAll('img');
    var best = null, bestArea = 0;
    for (var i = 0; i < imgs.length; i++) {
      var r = imgs[i].getBoundingClientRect();
      var area = r.width * r.height;
      if (area > bestArea && r.width > 0 && r.bottom > 0 && r.top < innerHeight) {
        best = imgs[i];
        bestArea = area;
      }
    }
    return best;
  }

  function imageFor(scope) {
    if (!scope) return null;

    var img = biggestImage(scope) || scope.querySelector('img');
    if (img && (img.currentSrc || img.src)) return img.currentSrc || img.src;

    var source = scope.querySelector('source[srcset]');
    if (source) {
      var first = source.getAttribute('srcset').split(',')[0].trim().split(' ')[0];
      if (first) return first;
    }

    var nodes = scope.querySelectorAll('*');
    for (var i = 0; i < nodes.length && i < 40; i++) {
      var bg = getComputedStyle(nodes[i]).backgroundImage;
      if (bg && bg.indexOf('url(') === 0) return bg.slice(4, -1).replace(/["']/g, '');
    }
    return null;
  }

  function fly(scope, sourceEl) {
    if (reduceMotion) return;

    var destination = document.querySelector(CART_TARGET);
    if (!destination) return;

    var origin = sourceEl || scope;
    if (!origin) return;

    var from = origin.getBoundingClientRect();
    var to = destination.getBoundingClientRect();
    if (!from.width || !to.width) return;

    var startX = from.left + from.width / 2;
    var startY = from.top + from.height / 2;
    var endX = to.left + to.width / 2;
    var endY = to.top + to.height / 2;

    var tile = document.createElement('div');
    tile.className = 'hqa-fly';

    var src = imageFor(scope);
    if (src) tile.style.backgroundImage = 'url("' + src + '")';

    var size = Math.max(44, Math.min(72, Math.round(from.width * 0.32)));
    tile.style.setProperty('--width', size + 'px');
    tile.style.setProperty('--height', size + 'px');
    tile.style.setProperty('--start-x', startX + 'px');
    tile.style.setProperty('--start-y', startY + 'px');
    tile.style.setProperty('--travel-x', (endX - startX) + 'px');
    tile.style.setProperty('--travel-y', (endY - startY) + 'px');

    document.body.appendChild(tile);

    var done = false;
    function cleanup() {
      if (done) return;
      done = true;
      tile.remove();
      destination.classList.add('hqa-cart-pulse');
      setTimeout(function () { destination.classList.remove('hqa-cart-pulse'); }, 420);
    }

    // Animations are not resolved on the frame the node is inserted. A timer is
    // used rather than rAF because rAF is throttled in background tabs.
    setTimeout(function () {
      var anims = tile.getAnimations ? tile.getAnimations() : [];
      if (anims.length) {
        Promise.allSettled(anims.map(function (a) { return a.finished; })).then(cleanup);
      }
    }, 30);

    setTimeout(cleanup, 1400);
  }

  /* ------------------------------------------------------------------ wiring */

  /* 1. Product cards in a grid */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var button = event.target.closest(BTN);
    if (!button || !button.classList.contains('hqa-button')) return;

    // Option products open the quick-cart modal to choose a variant. Nothing is
    // added yet, so neither the tick nor the flight belongs here.
    if (button.classList.contains('quick-cart-drawer__trigger')) return;

    var card = button.closest(CARD) || button.closest('li');
    var media = card ? card.querySelector(MEDIA) || card.querySelector('img') : null;

    confirm(button);
    fly(card, media || card);
  }, true);

  /* 2. The quick-cart modal (variant products) */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var t = event.target.closest(
      'quick-cart-drawer button[name="add"], quick-cart-drawer button[type="submit"], quick-cart-drawer .product-card__add-to-cart--button'
    );
    if (!t) return;
    var drawer = t.closest('quick-cart-drawer');
    var media = drawer ? drawer.querySelector('.quick-cart-drawer__slider-wrapper') : null;
    fly(media || drawer, biggestImage(media || drawer) || t);
  }, true);

  /* 3. The main product page */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var t = event.target.closest(PDP_BTN);
    if (!t) return;
    if (t.closest('quick-cart-drawer') || t.closest(CARD)) return;

    var scope = document.querySelector(PDP_MEDIA);
    var media = biggestImage(scope) || scope;
    fly(scope || document.body, media || t);
  }, true);

  /* --------------------------------------------------------------- lifecycle */

  function boot() {
    enhanceAll(document);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureAll);
    }
    setTimeout(measureAll, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measureAll, 200);
  });

  var mo = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(BTN)) enhance(node);
        else if (node.querySelectorAll) enhanceAll(node);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (e) { enhanceAll(e.target); });
})();
